import { FC, useCallback, useEffect } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { v4 } from 'uuid';
// i18next
import { useTranslation } from 'react-i18next';
// Types
import QuestionTypes from 'types/QuestionTypes';
import MediaFormats from 'types/MediaFormats';
import QuizTypes from 'types/QuizTypes';
// Components
import { Input } from 'components/Controls';
import MultiLanguageTextField from 'components/MultiLanguageTextField';
import ImageUpload from 'components/ImageUpload';
import { Button, IconButton } from 'components/Buttons';
import TimeoutField from '../TimeoutField';
// MUI
import { Tooltip } from '@material-ui/core';
// Icons
import { FaTrashAlt } from 'react-icons/fa';
// Utilities
import { isRequired, noWhitespace } from 'utilities/validation';

type Props = {
  questionRef: string;
}

const ConnectionsGridForm: FC<Props> = ({ questionRef }) => {
  const { t } = useTranslation();

  const { control, formState: { errors }, watch, setValue } = useFormContext();

  const watchedQuestions = watch('questions');
  const questionIndex = watchedQuestions.findIndex((question: any) => question.ref === questionRef);

  const { fields, append, remove } = useFieldArray({
    control,
    name: `questions.${questionIndex}.config.groups`,
  });

  const addGroup = () => {
    const group = {
      ref: v4(),
      items: Array.from({ length: 4 }).map(() => ({
        ref: v4(),
        image: '',
      })),
    };
    append(group);
  };

  const getQuestionError = useCallback((field: string) => {
    if (!errors || !errors.questions) return '';
    const question = (errors as any).questions[questionIndex];
    return question && question[field] ? question[field].message : '';
    // eslint-disable-next-line
  }, [errors]);

  const getGridImageError = useCallback((groupIndex: number, imageIndex: number) => {
    if (!errors || !errors.questions) return '';
    const group = (errors as any).questions[questionIndex]?.config?.groups?.[groupIndex];
    const image = group?.items?.[imageIndex];
    return image && image.image ? image.image.message : '';
    // eslint-disable-next-line
  }, [errors]);

  const watchedQuestionType = watch(`questions[${questionIndex}].type`);
  const watchedQuestionTimeout = watch(`questions[${questionIndex}].timeout`);
  const watchedQuizType = watch('type');
  const defaultQuestionTimeout = watchedQuizType === QuizTypes.Prediction ? 30 : 10;

  useEffect(() => {
    if (watchedQuestionType === QuestionTypes.ShortText) {
      setValue(`questions[${questionIndex}].answers`, []);
    }
    // eslint-disable-next-line
  }, [watchedQuestionType]);

  const isDisabled = fields.length >= 4;

  if (questionIndex === undefined || questionIndex < 0) return null;

  return (
    <div className="w-full -mx-6 -y-4 px-6 pb-3">
      <MultiLanguageTextField
        rules={{ required: isRequired, validate: noWhitespace }}
        name={`questions.${questionIndex}.title`}
        label={t('labels.question')}
        required={true}
        error={Boolean(getQuestionError('title'))}
        errorText={getQuestionError('title')}
        customKey={`questions.${questionRef}.title`}
        className="mb-4"
      />
      <Controller
        control={control} name={`questions.${questionIndex}.score`}
        render={({ field: { onChange, value } }) => (
          <Input
            type="number"
            label={t('labels.score')} id={`questions.${questionIndex}.score`} name={`questions.${questionIndex}.score`}
            value={value}
            onChange={onChange}
            helperText={watchedQuestionTimeout
              ? t('pages.quizForm.questionScoreHintWithTimeout', { score: (watchedQuestionTimeout || defaultQuestionTimeout) * 100 })
              : t('pages.quizForm.questionScoreHint', { score: watchedQuizType === QuizTypes.Prediction ? '3,000' : '1,000' })
            }
          />
        )}
      />
      <TimeoutField questionIndex={questionIndex} />
      <div className="mb-4 flex items-center justify-between gap-4">
        <h3 className="text-sm text-white font-semibold">{t('labels.images')}</h3>
        <Tooltip title={isDisabled ? 'Maximum groups reached' : ''}>
          <Button
            variant="contained" size="sm"
            onClick={addGroup}
            disabled={isDisabled}
          >{t('buttons.addGroup')}</Button>
        </Tooltip>
      </div>
      <div className='flex gap-4 flex-wrap'>
        {fields.map((field: any, index) => (
          <div key={field.id} className="mb-2">
            <div className="mb-2 flex items-center gap-2">
              <p className="text-white text-sm font-semibold">{`${t('labels.group')} ${index + 1}`}</p>
              <IconButton onClick={() => remove(index)} size="sm">
                <FaTrashAlt />
              </IconButton>
            </div>
            <MultiLanguageTextField
              name={`questions.${questionIndex}.config.groups.${index}.title`}
              label={t('labels.title')}
              customKey={`questions.${questionRef}.config.groups.${index}.title`}
              className="mb-4"
            />
            <div className="grid grid-cols-4 gap-3">
              {field.items.map((item: any, itemIndex: number) => (
                <Controller
                  key={item.ref}
                  control={control}
                  name={`questions.${questionIndex}.config.groups.${index}.items.${itemIndex}.image`}
                  rules={{ required: isRequired }}
                  render={({ field }) => (
                    <ImageUpload
                      {...field}
                      format={MediaFormats.GridImage}
                      required={true}
                      error={Boolean(getGridImageError(index, itemIndex))}
                      errorText={getGridImageError(index, itemIndex)}
                    />
                  )}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ConnectionsGridForm;
